import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PlatformId } from "views/page-content/platforms/types";
import { RootState } from "store/reducers";
import { FacebookEdge } from "api/facebook/types";
import {
    AddDestinationsModalSteps,
    AddDestinationsSubmissionStatus
} from "./types";

// Trimmed version of NewLivestreamModal's useNewLivestreamStepLogic covering
// only the steps that apply once a livestream is already scheduled: pick
// destinations, configure platform-specific settings, then connect them.
export const useAddDestinationsStepLogic = ({
    existingDestinationIds,
    onClose
}: {
    existingDestinationIds: Set<string>;
    onClose: () => void;
}) => {
    const { t } = useTranslation();

    const { general } = useSelector((state: RootState) => state.platforms);

    const [activeStep, setActiveStep] = useState<AddDestinationsModalSteps>(
        AddDestinationsModalSteps.SelectDestination
    );

    const [selectedPlatform, setSelectedPlatform] = useState<PlatformId | null>(
        null
    );

    const [selectedDestinationId, setSelectedDestinationId] = useState<
        string | null
    >(null);

    const [currentFacebookEdge, setCurrentFacebookEdge] =
        useState<FacebookEdge>(null);

    const [onAdvanceStep, setOnAdvanceStep] = useState<(() => void) | null>(
        null
    );
    const [onDismiss, setOnDismiss] = useState<(() => void) | null>(null);

    const [
        hasAddedPlatformSpecificSettings,
        setHasAddedPlatformSpecificSettings
    ] = useState<boolean>(false);

    const [submissionStatus, setSubmissionStatus] =
        useState<AddDestinationsSubmissionStatus>(
            AddDestinationsSubmissionStatus.Processing
        );

    const clearCallbacks = useCallback(() => {
        setOnAdvanceStep(null);
        setOnDismiss(null);
    }, []);

    const newlySelectedDestinations = useMemo(
        () =>
            general.generalForm.selectedSimulcastDestinations.filter(
                (d) => !existingDestinationIds.has(d.id)
            ),
        [general.generalForm.selectedSimulcastDestinations, existingDestinationIds]
    );

    const getPlatformSubsettingsModalHeader = useCallback(
        (platformId: PlatformId | null) => {
            if (!platformId) return null;

            switch (platformId) {
                case PlatformId.Youtube:
                    return t("platforms:channel-settings");
                case PlatformId.Twitch:
                    return t("platforms:twitch");
                case PlatformId.Facebook:
                    switch (currentFacebookEdge) {
                        case "accounts":
                            return t("facebook-destination:page-settings");
                        case "me":
                            return t("facebook-destination:timeline-settings");
                        default:
                            return t(`platforms:facebook`);
                    }
                default:
                    return t(`platforms:${platformId}`);
            }
        },
        [t, currentFacebookEdge]
    );

    const advanceStep = useCallback(() => {
        if (onAdvanceStep) onAdvanceStep();

        switch (activeStep) {
            case AddDestinationsModalSteps.SelectDestination:
                setSubmissionStatus(AddDestinationsSubmissionStatus.Processing);
                setActiveStep(AddDestinationsModalSteps.Processing);
                break;
            case AddDestinationsModalSteps.PlatformSettings:
                setActiveStep(AddDestinationsModalSteps.SelectDestination);
                setHasAddedPlatformSpecificSettings(false);
                setSelectedDestinationId(null);
                break;
            case AddDestinationsModalSteps.Processing:
                // "Done" - only reachable once processing has finished.
                onClose();
                break;
        }

        clearCallbacks();
    }, [activeStep, onAdvanceStep, clearCallbacks, onClose]);

    const advanceButtonText = useMemo(() => {
        switch (activeStep) {
            case AddDestinationsModalSteps.SelectDestination:
                return t("add-destinations-modal:add-button");
            case AddDestinationsModalSteps.PlatformSettings:
                return t("buttons:save");
            case AddDestinationsModalSteps.Processing:
                if (
                    submissionStatus === AddDestinationsSubmissionStatus.Processing
                ) {
                    return null;
                }
                return t("buttons:done");
            default:
                return t("buttons:back");
        }
    }, [activeStep, t, submissionStatus]);

    const dismissButtonText = useMemo(() => {
        switch (activeStep) {
            case AddDestinationsModalSteps.SelectDestination:
                return t("buttons:cancel");
            case AddDestinationsModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings
                    ? t("buttons:back")
                    : t("buttons:cancel");
            case AddDestinationsModalSteps.Processing:
                // Once processing finishes, "Done" (the primary button) is
                // the only action - hide Cancel so it isn't shown alongside it.
                return submissionStatus === AddDestinationsSubmissionStatus.Processing
                    ? t("buttons:cancel")
                    : null;
            default:
                return t("buttons:cancel");
        }
    }, [t, activeStep, hasAddedPlatformSpecificSettings, submissionStatus]);

    const dismissButtonAction = useCallback(() => {
        if (onDismiss) onDismiss();
        switch (activeStep) {
            case AddDestinationsModalSteps.SelectDestination:
                onClose();
                break;
            case AddDestinationsModalSteps.PlatformSettings:
                if (!hasAddedPlatformSpecificSettings) {
                    setActiveStep(AddDestinationsModalSteps.SelectDestination);
                }
                setHasAddedPlatformSpecificSettings(false);
                setSelectedDestinationId(null);
                setCurrentFacebookEdge(null);
                break;
            case AddDestinationsModalSteps.Processing:
                // Only reachable while still processing (dismissButtonText
                // hides Cancel once finished) - closing here just leaves
                // whatever already connected in place.
                onClose();
                break;
        }
        clearCallbacks();
    }, [activeStep, hasAddedPlatformSpecificSettings, onDismiss, onClose, clearCallbacks]);

    const canProceedToNextStep = useMemo(() => {
        switch (activeStep) {
            case AddDestinationsModalSteps.SelectDestination:
                return newlySelectedDestinations.length > 0;
            case AddDestinationsModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings;
            case AddDestinationsModalSteps.Processing:
                return true;
            default:
                return false;
        }
    }, [activeStep, newlySelectedDestinations.length, hasAddedPlatformSpecificSettings]);

    const modalHeaderText = useMemo(() => {
        switch (activeStep) {
            case AddDestinationsModalSteps.SelectDestination:
            case AddDestinationsModalSteps.Processing:
                return t("add-destinations-modal:header");
            case AddDestinationsModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings
                    ? getPlatformSubsettingsModalHeader(selectedPlatform)
                    : t(`platforms:${selectedPlatform}`);
            default:
                return t("add-destinations-modal:header");
        }
    }, [
        activeStep,
        t,
        selectedPlatform,
        hasAddedPlatformSpecificSettings,
        getPlatformSubsettingsModalHeader
    ]);

    return {
        activeStep,
        setActiveStep,
        selectedPlatform,
        setSelectedPlatform,
        selectedDestinationId,
        setSelectedDestinationId,
        setCurrentFacebookEdge,
        setHasAddedPlatformSpecificSettings,
        setOnAdvanceStep,
        setOnDismiss,
        advanceStep,
        advanceButtonText,
        dismissButtonAction,
        dismissButtonText,
        canProceedToNextStep,
        modalHeaderText,
        newlySelectedDestinations,
        submissionStatus,
        setSubmissionStatus
    };
};
